import { FormatOptions, LocaleCode, formatDateColumnValue } from '@doss/common-all'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { FormatPreset } from '~/common/generated/apollo-operations'
import { TranslationKeys } from '~/common/generated/i18n.generated'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import {
  DatePreset,
  DATE_PATTERNS,
  DATE_TIME_PATTERNS,
  findSelectedPreset,
  getDatePattern,
} from './datePresets'

const DATE_TOKEN_ROWS: ReadonlyArray<{ tokens: string; labelKey: TranslationKeys }> = [
  { tokens: 'YYYY', labelKey: 'table.create_column.format.date_tokens.year' },
  { tokens: 'MMMM / MMM / MM', labelKey: 'table.create_column.format.date_tokens.month' },
  { tokens: 'DD / D', labelKey: 'table.create_column.format.date_tokens.day' },
  { tokens: 'dddd / ddd', labelKey: 'table.create_column.format.date_tokens.weekday' },
]

const TIME_TOKEN_ROWS: ReadonlyArray<{ tokens: string; labelKey: TranslationKeys }> = [
  { tokens: 'HH / hh', labelKey: 'table.create_column.format.date_tokens.hour' },
  { tokens: 'mm', labelKey: 'table.create_column.format.date_tokens.minutes' },
  { tokens: 'A / a', labelKey: 'table.create_column.format.date_tokens.meridiem' },
]

const DATE_PRESETS: ReadonlyArray<{ value: DatePreset; labelKey: TranslationKeys }> = [
  {
    value: DatePreset.DEFAULT,
    labelKey: 'table.create_column.format.date_preset_options.default',
  },
  { value: DatePreset.ISO, labelKey: 'table.create_column.format.date_preset_options.iso' },
  { value: DatePreset.US, labelKey: 'table.create_column.format.date_preset_options.us' },
  { value: DatePreset.EU, labelKey: 'table.create_column.format.date_preset_options.eu' },
  { value: DatePreset.LONG, labelKey: 'table.create_column.format.date_preset_options.long' },
  { value: DatePreset.CUSTOM, labelKey: 'table.create_column.format.date_preset_options.custom' },
]

export type DateFormatValue = {
  formatPreset: FormatPreset
  formatOptions: FormatOptions
}

interface DateFormatFieldsProps {
  /** Current preset (null when the input has no formatting yet — renders the locale default) */
  formatPreset: FormatPreset | null
  /** Current options; carries the dayjs `pattern` when the preset is DATE_CUSTOM */
  formatOptions: FormatOptions | null
  /** Whether the value carries a time component (drives the built-in pattern set and token help) */
  isDateTime: boolean
  /** Called with the fully-resolved preset + options whenever the user changes anything */
  onChange: (value: DateFormatValue) => void
  /** Organization locale used to render the preset preview strings */
  orgLocale?: LocaleCode
}

/**
 * Headless format configuration for DATE/DATE_TIME values: a preset dropdown (Default, ISO, US, EU,
 * Long, Custom) plus a custom dayjs-pattern input with a live preview and token reference. Fully
 * controlled — it holds no form-library state, so both the Formik (tables) and react-hook-form
 * (forms) hosts can drive it through `formatPreset`/`formatOptions`/`onChange`. Every non-empty
 * selection persists as the shared FormatPreset.DATE_CUSTOM preset with a `pattern` option; an empty
 * pattern (the Default preset) falls back to the org locale's rendering.
 */
export const DateFormatFields = ({
  formatPreset,
  formatOptions,
  isDateTime,
  onChange,
  orgLocale,
}: DateFormatFieldsProps) => {
  const { t } = useTypedTranslation()
  const locale = orgLocale ?? LocaleCode.EN_US
  const previewValue = useMemo(() => dayjs().format(), [])
  const pattern = getDatePattern(formatOptions)
  const patterns = isDateTime ? DATE_TIME_PATTERNS : DATE_PATTERNS
  const tokenRows = isDateTime ? [...DATE_TOKEN_ROWS, ...TIME_TOKEN_ROWS] : DATE_TOKEN_ROWS

  // Derive the selected preset from props so external updates (e.g. inheriting a linked column's
  // format) reflect immediately. `forceCustom` keeps the custom pattern input open while the user
  // edits it, even when the current pattern is empty or happens to match a built-in preset.
  const [forceCustom, setForceCustom] = useState(false)
  const derivedPreset =
    formatPreset === FormatPreset.DATE_CUSTOM
      ? (findSelectedPreset(pattern, isDateTime) ?? DatePreset.CUSTOM)
      : DatePreset.DEFAULT
  const preset = forceCustom ? DatePreset.CUSTOM : derivedPreset

  // Drop the forced-custom view whenever formatting is cleared externally (e.g. switching between
  // DATE and DATE_TIME resets the preset), so the dropdown doesn't linger on Custom with an empty
  // pattern while the effective value is the locale default.
  useEffect(() => {
    if (formatPreset !== FormatPreset.DATE_CUSTOM) {
      setForceCustom(false)
    }
  }, [formatPreset])

  const presetOptions = useMemo(
    () =>
      DATE_PRESETS.map(({ value, labelKey }) => {
        if (value === DatePreset.CUSTOM) {
          return {
            value,
            label: t(labelKey),
          }
        }

        return {
          value,
          label: t(labelKey),
          endIcon: (
            <code className="text-muted-foreground">
              {formatDateColumnValue(
                previewValue,
                {
                  formatPreset: FormatPreset.DATE_CUSTOM,
                  formatOptions: { pattern: patterns[value] },
                  isDateTime,
                },
                locale,
              )}
            </code>
          ),
        }
      }),
    [isDateTime, locale, patterns, previewValue, t],
  )

  const setPattern = (nextPattern: string) => {
    onChange({
      formatPreset: FormatPreset.DATE_CUSTOM,
      formatOptions: { pattern: nextPattern } satisfies FormatOptions,
    })
  }

  const onPresetChange = (next: DatePreset | undefined) => {
    if (!next) {
      return
    }

    if (next === DatePreset.CUSTOM) {
      setForceCustom(true)
      setPattern(pattern ?? '')
      return
    }

    setForceCustom(false)
    setPattern(patterns[next])
  }

  const preview = pattern
    ? formatDateColumnValue(
        previewValue,
        {
          formatPreset: FormatPreset.DATE_CUSTOM,
          formatOptions: { pattern },
          isDateTime,
        },
        locale,
      )
    : ''

  return (
    <div className="flex flex-col gap-3">
      <h4>{t('table.create_column.format.section_title')}</h4>
      <Dropdown
        label={t('table.create_column.format.preset')}
        onChange={onPresetChange}
        options={presetOptions}
        showClearButton={false}
        value={preset}
      />
      {preset === DatePreset.CUSTOM && (
        <>
          <ShortTextInput
            label={t('table.create_column.format.custom_date_format')}
            onChange={(event) => setPattern(event.target.value)}
            required
            value={pattern ?? ''}
          />
          <div className="flex items-center justify-between gap-2 rounded border border-dashed border-gray-4 bg-gray-2 px-3 py-2 text-xs">
            <span>{t('table.create_column.format.preview')}</span>
            <code className="text-gray-12">{preview}</code>
          </div>

          <div className="flex flex-col gap-1.5">
            <h4>{t('table.create_column.format.tokens')}</h4>
            {tokenRows.map(({ tokens, labelKey }) => (
              <div key={tokens} className="grid grid-cols-[9rem_1fr] gap-3 text-xs">
                <code className="rounded bg-gray-2 px-2 py-1 text-gray-12">{tokens}</code>
                <p className="py-1 text-muted-foreground">{t(labelKey)}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
